import { PlatformDemoDelete } from './PlatformDemoDelete'
import { PlatformDemoSignIns, type DemoPersona } from './PlatformDemoSignIns'
import { PlatformConnectionRetention } from './PlatformConnectionRetention'
import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Ban, UserMinus } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { AppShell } from '@/components/templates/app-shell'
import { DetailPage } from '@/components/templates/detail-page'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ApiError, deleteJSON, getJSON, patchJSON, postJSON } from '@/lib/api'
import {
  statusLabel,
  statusVariant,
  type AssociationStatus,
  type PlatformAssociation,
} from '@/pages/PlatformAssociationsListPage'
import { PlatformConnectionSummary } from '@/pages/PlatformConnectionSummary'
import { platformAdminNav } from '@/pages/platformAdminNav'
import { demoSeedPollInterval, PlatformDemoSeedStatus } from './PlatformDemoSeedStatus'
import type { PlatformSurface } from '@/pages/PlatformAdminSignInPage'

interface PlatformSeat {
  id: number
  role: string
  user: {
    id: number
    name: string
    email: string
  }
}

export interface PlatformAssociationDetail extends PlatformAssociation {
  demo_personas?: DemoPersona[]
  seats: PlatformSeat[]
  default_platform_fee_rate_bp: number
  platform_fee_rate_override_bp: number | null
  effective_platform_fee_rate_bp: number
}

interface AssociationDetailResponse {
  association: PlatformAssociationDetail
}

interface PlatformAssociationDetailPageProps {
  platform: PlatformSurface
  associationId: number
  unauthorizedFallback?: React.ReactNode
}

export default function PlatformAssociationDetailPage({
  platform,
  associationId,
  unauthorizedFallback,
}: PlatformAssociationDetailPageProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const associationQuery = useQuery({
    queryKey: ['platform-association', associationId],
    queryFn: () =>
      getJSON<AssociationDetailResponse>(`/api/v1/platform/associations/${associationId}`),
    retry: false,
    refetchInterval: ({ state }) => demoSeedPollInterval(state.data?.association, state.error),
  })
  const refreshAssociation = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['platform-association', associationId] }),
      queryClient.invalidateQueries({ queryKey: ['platform-associations'] }),
    ])
  }
  const updateStatus = useMutation({
    mutationFn: (status: AssociationStatus) =>
      patchJSON<AssociationDetailResponse>(`/api/v1/platform/associations/${associationId}`, {
        status,
      }),
    onSuccess: refreshAssociation,
  })
  const revokeSeat = useMutation({
    mutationFn: (userId: number) =>
      deleteJSON(`/api/v1/platform/associations/${associationId}/seats/${userId}`),
    onSuccess: refreshAssociation,
  })

  if (
    associationQuery.error instanceof ApiError &&
    associationQuery.error.status === 401 &&
    unauthorizedFallback
  ) {
    return unauthorizedFallback
  }

  return (
    <AppShell
      associationName={platform.brand}
      nav={platformAdminNav}
      user={{ name: 'Platform Admin' }}
    >
      {associationQuery.isLoading && <EmptyState title="Loading Association" />}
      {associationQuery.isError && !associationQuery.isLoading && (
        <EmptyState title="Association unavailable" description="Refresh and try again." />
      )}
      {associationQuery.isSuccess && (
        <AssociationDetail
          association={associationQuery.data.association}
          statusPending={updateStatus.isPending}
          revokePendingUserId={revokeSeat.variables}
          revokePending={revokeSeat.isPending}
          onBack={() => navigate('/platform/associations')}
          onStatusChange={(status) => updateStatus.mutate(status)}
          onRevokeSeat={(userId) => revokeSeat.mutate(userId)}
        />
      )}
    </AppShell>
  )
}

function AssociationDetail({
  association,
  statusPending,
  revokePendingUserId,
  revokePending,
  onBack,
  onStatusChange,
  onRevokeSeat,
}: {
  association: PlatformAssociationDetail
  statusPending: boolean
  revokePendingUserId?: number
  revokePending: boolean
  onBack: () => void
  onStatusChange: (status: AssociationStatus) => void
  onRevokeSeat: (userId: number) => void
}) {
  const suspended = association.status === 'suspended'
  const actionLabel = suspended ? 'Restore' : 'Suspend'
  const nextStatus = suspended ? 'active' : 'suspended'

  return (
    <DetailPage
      title={association.name}
      subtitle={`${association.web_address ?? association.web_address_name} · ${association.seat_count} seats`}
      backHref="/platform/associations"
      backLabel="Associations"
      onBack={onBack}
      badge={
        <span className="inline-flex items-center gap-2">
          <Badge variant={statusVariant[association.status]}>
            {statusLabel[association.status]}
          </Badge>
          {association.demo && (
            <Badge variant="neutral" className="bg-muted ring-1 ring-inset ring-border">
              Demo
            </Badge>
          )}
        </span>
      }
      actions={
        <Button
          variant={suspended ? 'outline' : 'quietDestructive'}
          disabled={statusPending}
          onClick={() => onStatusChange(nextStatus)}
        >
          {!suspended && <Ban className="mr-1.5 h-4 w-4" aria-hidden="true" />}
          {actionLabel}
        </Button>
      }
    >
      {association.demo && <PlatformDemoSeedStatus association={association} />}
      <dl className="mb-6 flex items-center gap-3">
        <dt className="text-body-sm text-muted-foreground">Connections</dt>
        <dd>
          <PlatformConnectionSummary summary={association.connection_summary} />
        </dd>
      </dl>
      <PlatformConnectionRetention connections={association.connection_summary?.connections} />
      <PlatformFeeSettings association={association} />
      {association.demo && <PlatformDemoSignIns key={association.id} association={association} />}
      <div className="flex items-center justify-between pb-3">
        <h2 className="text-subheading font-semibold text-foreground">Seats</h2>
        <GrantSeatDialog associationId={association.id} />
      </div>
      {association.seats.length === 0 ? (
        <EmptyState title="No Seats yet" />
      ) : (
        <div className="overflow-hidden rounded-md border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="w-24" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {association.seats.map((seat) => (
                <TableRow key={seat.id}>
                  <TableCell className="text-foreground">{seat.user.name}</TableCell>
                  <TableCell className="text-muted-foreground">{seat.user.email}</TableCell>
                  <TableCell className="text-muted-foreground">{seat.role}</TableCell>
                  <TableCell>
                    <Button
                      variant="quietDestructive"
                      size="sm"
                      disabled={revokePending && revokePendingUserId === seat.user.id}
                      onClick={() => onRevokeSeat(seat.user.id)}
                    >
                      <UserMinus className="mr-1.5 h-4 w-4" aria-hidden="true" />
                      Revoke
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {association.demo && <PlatformDemoDelete key={association.id} association={association} />}
    </DetailPage>
  )
}

function PlatformFeeSettings({ association }: { association: PlatformAssociationDetail }) {
  const queryClient = useQueryClient()
  const [defaultRate, setDefaultRate] = React.useState(
    association.default_platform_fee_rate_bp.toString(),
  )
  const [overrideRate, setOverrideRate] = React.useState(
    association.platform_fee_rate_override_bp?.toString() ?? '',
  )
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['platform-association', association.id] })
  const updateDefault = useMutation({
    mutationFn: () =>
      patchJSON('/api/v1/platform/platform_fee_settings', {
        default_rate_basis_points: defaultRate,
      }),
    onSuccess: refresh,
  })
  const updateOverride = useMutation({
    mutationFn: (rate: string | null) =>
      patchJSON<AssociationDetailResponse>(`/api/v1/platform/associations/${association.id}`, {
        platform_fee_rate_override_bp: rate,
      }),
    onSuccess: refresh,
  })

  React.useEffect(() => {
    setDefaultRate(association.default_platform_fee_rate_bp.toString())
    setOverrideRate(association.platform_fee_rate_override_bp?.toString() ?? '')
  }, [association])

  const error = updateDefault.error ?? updateOverride.error

  return (
    <Card className="mb-5 max-w-2xl">
      <CardHeader className="p-4 pb-3">
        <CardTitle className="text-body font-semibold">Platform Fee</CardTitle>
        <CardDescription>
          Effective rate · {association.effective_platform_fee_rate_bp} bp
        </CardDescription>
      </CardHeader>
      <CardContent className="divide-y divide-border border-t border-border p-0">
        <form
          className="flex items-end gap-2 px-4 py-3"
          onSubmit={(event) => {
            event.preventDefault()
            updateDefault.mutate()
          }}
        >
          <div className="flex flex-1 flex-col gap-1.5">
            <Label htmlFor="platform-fee-default">Platform-wide default</Label>
            <Input
              id="platform-fee-default"
              inputMode="numeric"
              value={defaultRate}
              onChange={(event) => setDefaultRate(event.target.value)}
            />
          </div>
          <Button type="submit" size="sm" variant="outline" disabled={updateDefault.isPending}>
            Save default
          </Button>
        </form>
        <form
          className="flex items-end gap-2 px-4 py-3"
          onSubmit={(event) => {
            event.preventDefault()
            updateOverride.mutate(overrideRate)
          }}
        >
          <div className="flex flex-1 flex-col gap-1.5">
            <Label htmlFor="platform-fee-override">Association override</Label>
            <Input
              id="platform-fee-override"
              inputMode="numeric"
              placeholder="Use default"
              value={overrideRate}
              onChange={(event) => setOverrideRate(event.target.value)}
            />
          </div>
          {association.platform_fee_rate_override_bp === null ? (
            <Button
              type="submit"
              size="sm"
              variant="outline"
              disabled={updateOverride.isPending || !overrideRate}
            >
              Set override
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={updateOverride.isPending}
              onClick={() => updateOverride.mutate(null)}
            >
              Use default
            </Button>
          )}
        </form>
        {error && <p className="px-4 py-3 text-body-sm text-destructive">{error.message}</p>}
      </CardContent>
    </Card>
  )
}

function GrantSeatDialog({ associationId }: { associationId: number }) {
  const queryClient = useQueryClient()
  const [open, setOpen] = React.useState(false)
  const [userId, setUserId] = React.useState('')
  const grantSeat = useMutation({
    mutationFn: () =>
      postJSON(`/api/v1/platform/associations/${associationId}/seats`, {
        user_id: Number(userId),
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['platform-association', associationId] }),
        queryClient.invalidateQueries({ queryKey: ['platform-associations'] }),
      ])
      setUserId('')
      setOpen(false)
    },
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">Grant seat</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Grant seat</DialogTitle>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            grantSeat.mutate()
          }}
        >
          <div className="flex flex-col gap-2">
            <Label htmlFor="seat-user-id">User ID</Label>
            <Input
              id="seat-user-id"
              inputMode="numeric"
              value={userId}
              onChange={(event) => setUserId(event.target.value)}
            />
          </div>
          {grantSeat.error && (
            <p className="text-body-sm text-destructive">{grantSeat.error.message}</p>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={grantSeat.isPending || userId.length === 0}>
              Grant
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
